import { GovIcon } from '@gov-design-system-ce/react';
import { GovFormWeb } from '@gov-design-system-ce/react-form';
import type { CmsExternalServiceLink } from '@gov-nx/api';
import type { FormDefinition2 } from '@gov-nx/core/service';
import { useGlobalLocale } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import type { Data, Fields } from '@gov-nx/module/service';
import { Button, LayoutGap } from '@gov-nx/ui/web';
import { RezaChangeFormView } from '../../Reza/RezaChangeFormView/RezaChangeFormView';
import { MarkdownRender } from '../../Markdown/MarkdownRender';

interface FormEngineFormViewProps {
	form: FormDefinition2<Data, Fields>;
	onSubmit: () => void;
	isEligibleListLoading: boolean;
	isFormSessionLoading: boolean;
	hasRepresentationOptions: boolean;
	isRepresentationAllowed: boolean;
	isSubmitButtonDisabled: boolean;
	role?: Data['role'];
	link?: CmsExternalServiceLink;
	redirectUrl: Nullable<string>;
	formId?: string;
}

export const FormEngineFormView = ({
	form,
	onSubmit,
	isEligibleListLoading,
	isFormSessionLoading,
	hasRepresentationOptions,
	isRepresentationAllowed,
	isSubmitButtonDisabled,
	role,
	link,
	redirectUrl,
	formId,
}: FormEngineFormViewProps) => {
	const { t } = useGlobalLocale();

	return (
		<GovFormWeb
			id={formId}
			formDefinition={form}
			onSubmit={onSubmit}>
			{redirectUrl ? (
				<MarkdownRender
					includeProtocolInLinkLabels={true}
					openExternalLinksInSameTab={true}
					forceFullPageNavigation={true}
					applyClasses={{
						paragraph: 'text-body-m! text-(--text-secondary)',
						link: 'break-all',
					}}>
					{t('akce.pokracovat-na-formular-odkaz', {
						link: redirectUrl,
					})}
				</MarkdownRender>
			) : (
				<LayoutGap className="mb-8">
					<RezaChangeFormView
						form={form}
						isEligibleListLoading={isEligibleListLoading}
						isRepresentationAllowed={isRepresentationAllowed}
						hasRepresentationOptions={hasRepresentationOptions}
						role={role}
						strings={{
							representationNotAllowed: t('zpravy.zastupovani-nepovoleno'),
							representationListNotFound: t(
								'zpravy.zastupovani-seznam-nenalezen'
							),
						}}
					/>
					{link ? (
						<Button
							nativeType="submit"
							isLoading={isFormSessionLoading}
							isDisabled={isSubmitButtonDisabled}
							type={link.properties.type}
							color={link.properties.color}
							size={link.properties.size}
							aria-label={link.label}
							iconEnd={
								<GovIcon
									type="components"
									name="box-arrow-up-right"
								/>
							}>
							{link.label}
						</Button>
					) : null}
				</LayoutGap>
			)}
		</GovFormWeb>
	);
};
